import moment from 'moment-timezone';
import { mod } from '@densityco/lib-common-helpers';
import { DayOfWeek } from '@densityco/lib-common-types';

// ----------------------------------------------------------------------------
// DATE RANGE OPERATIONS
// ----------------------------------------------------------------------------
export enum DateRangeType {
  ABSOLUTE = 'ABSOLUTE',
  RELATIVE = 'RELATIVE',
}

export enum DateRangeRelativeUnit {
  DAYS = 'days',
  WEEKS = 'weeks',
  MONTHS = 'months',
  QUARTERS = 'quarters',
  YEARS = 'years',
}

export enum DateRangeRelativeDurationRound {
  START = 'START',
  END = 'END',
}

export type DateRangeRelativeDuration = {
  magnitude: number;
  unit: DateRangeRelativeUnit;
  round?: DateRangeRelativeDurationRound;
};

export type DateRangeRelative = {
  type: DateRangeType.RELATIVE;
  start: DateRangeRelativeDuration;
  end: DateRangeRelativeDuration;
  label?: string; // Optional, used to name a given date range
};

export type DateRangeAbsolute = {
  type: DateRangeType.ABSOLUTE;
  startDate: string;
  endDate: string;
};

export type DateRange = DateRangeAbsolute | DateRangeRelative;

function makeDuration(
  magnitude: number,
  unit: DateRangeRelativeUnit,
  round?: DateRangeRelativeDurationRound
): DateRangeRelativeDuration {
  return { magnitude, unit, round };
}

// A list of predefined date ranges, using these avoids having to build custom date ranges for every
// use case.
export const DATE_RANGES: { [key: string]: DateRangeRelative } = {
  TODAY: {
    label: 'Today',
    type: DateRangeType.RELATIVE,
    start: makeDuration(0, DateRangeRelativeUnit.DAYS, DateRangeRelativeDurationRound.START),
    end: makeDuration(0, DateRangeRelativeUnit.DAYS, DateRangeRelativeDurationRound.END),
  },
  YESTERDAY: {
    label: 'Yesterday',
    type: DateRangeType.RELATIVE,
    start: makeDuration(-1, DateRangeRelativeUnit.DAYS, DateRangeRelativeDurationRound.START),
    end: makeDuration(-1, DateRangeRelativeUnit.DAYS, DateRangeRelativeDurationRound.END),
  },
  LAST_7_DAYS: {
    label: 'Last 7 Days',
    type: DateRangeType.RELATIVE,
    start: makeDuration(-7, DateRangeRelativeUnit.DAYS),
    end: makeDuration(0, DateRangeRelativeUnit.DAYS),
  },
  LAST_WEEK: {
    label: 'Last Week',
    type: DateRangeType.RELATIVE,
    start: makeDuration(-1, DateRangeRelativeUnit.WEEKS, DateRangeRelativeDurationRound.START),
    end: makeDuration(-1, DateRangeRelativeUnit.WEEKS, DateRangeRelativeDurationRound.END),
  },
  WEEK_TO_DATE: {
    label: 'Week-to-Date',
    type: DateRangeType.RELATIVE,
    start: makeDuration(0, DateRangeRelativeUnit.WEEKS, DateRangeRelativeDurationRound.START),
    end: makeDuration(0, DateRangeRelativeUnit.DAYS),
  },
  LAST_4_WEEKS: {
    label: 'Last 4 Weeks',
    type: DateRangeType.RELATIVE,
    start: makeDuration(-4, DateRangeRelativeUnit.WEEKS, DateRangeRelativeDurationRound.START),
    end: makeDuration(-1, DateRangeRelativeUnit.WEEKS, DateRangeRelativeDurationRound.END),
  },
  LAST_28_DAYS: {
    label: 'Last 28 Days',
    type: DateRangeType.RELATIVE,
    start: makeDuration(-28, DateRangeRelativeUnit.DAYS),
    end: makeDuration(0, DateRangeRelativeUnit.DAYS),
  },
  LAST_30_DAYS: {
    label: 'Last 30 Days',
    type: DateRangeType.RELATIVE,
    start: makeDuration(-30, DateRangeRelativeUnit.DAYS),
    end: makeDuration(0, DateRangeRelativeUnit.DAYS),
  },
  LAST_MONTH: {
    label: 'Last Month',
    type: DateRangeType.RELATIVE,
    start: makeDuration(-1, DateRangeRelativeUnit.MONTHS, DateRangeRelativeDurationRound.START),
    end: makeDuration(-1, DateRangeRelativeUnit.MONTHS, DateRangeRelativeDurationRound.END),
  },
  MONTH_TO_DATE: {
    label: 'Month-to-Date',
    type: DateRangeType.RELATIVE,
    start: makeDuration(0, DateRangeRelativeUnit.MONTHS, DateRangeRelativeDurationRound.START),
    end: makeDuration(0, DateRangeRelativeUnit.DAYS),
  },
  LAST_90_DAYS: {
    label: 'Last 90 Days',
    type: DateRangeType.RELATIVE,
    start: makeDuration(-90, DateRangeRelativeUnit.DAYS),
    end: makeDuration(0, DateRangeRelativeUnit.DAYS),
  },

  // Future time range example, not needed for analytics but an example of how we'd do this
  NEXT_WEEK: {
    label: 'Next Week',
    type: DateRangeType.RELATIVE,
    start: makeDuration(1, DateRangeRelativeUnit.WEEKS, DateRangeRelativeDurationRound.START),
    end: makeDuration(1, DateRangeRelativeUnit.WEEKS, DateRangeRelativeDurationRound.END),
  },
  NEXT_7_DAYS: {
    label: 'Next 7 Days',
    type: DateRangeType.RELATIVE,
    start: makeDuration(0, DateRangeRelativeUnit.DAYS),
    end: makeDuration(7, DateRangeRelativeUnit.DAYS),
  },
};

export const DAYS_OF_WEEK: Array<DayOfWeek> = [
  DayOfWeek.SUNDAY,
  DayOfWeek.MONDAY,
  DayOfWeek.TUESDAY,
  DayOfWeek.WEDNESDAY,
  DayOfWeek.THURSDAY,
  DayOfWeek.FRIDAY,
  DayOfWeek.SATURDAY,
];

export function realizeDateRange(
  dateRange: DateRange,
  timeZone: string,
  opts: { organizationalWeekStartDay?: DayOfWeek; now?: moment.Moment } = {}
): { startDate: moment.Moment; endDate: moment.Moment } {
  const organizationalWeekStartDay = opts.organizationalWeekStartDay || DayOfWeek.SUNDAY;
  const now = opts.now || moment.tz(timeZone);

  if (dateRange.type === DateRangeType.ABSOLUTE) {
    return {
      startDate: moment.tz(dateRange.startDate, timeZone).startOf('day'),
      endDate: moment.tz(dateRange.endDate, timeZone).endOf('day'),
    };
  } else {
    return {
      startDate: realizeRelativeDuration(dateRange.start, now, organizationalWeekStartDay).startOf('day'),
      endDate: realizeRelativeDuration(dateRange.end, now, organizationalWeekStartDay).endOf('day'),
    };
  }
}

export function realizeRelativeDuration(
  relativeDuration: DateRangeRelativeDuration,
  now: moment.Moment,
  organizationalWeekStartDay: DayOfWeek
): moment.Moment {
  let timestamp = now.clone().add(relativeDuration.magnitude, relativeDuration.unit);

  // Weeks are a special case because of "organization days of week start day" being a a concept
  // that can effect what day a week starts on
  if (relativeDuration.unit === DateRangeRelativeUnit.WEEKS) {
    let targetDay = DAYS_OF_WEEK.indexOf(organizationalWeekStartDay);
    // FIXME: this shouldn't be possible, but default to Sunday if input is bad/undefined
    if (targetDay === -1) {
      targetDay = 0;
    }

    // Week should start on the target day, and end at the end of 1 before the target day
    //  eg. [Wednesday @ 00:00, Tuesday @ 23:59] if target day is Wednesday
    let targetDayWeekStart = targetDay;
    let targetDayWeekEnd = mod(targetDay - 1, 7);

    switch (relativeDuration.round) {
      case DateRangeRelativeDurationRound.START:
        while (timestamp.day() !== targetDayWeekStart) {
          timestamp.subtract(1, 'day');
        }
        timestamp = timestamp.startOf('day');
        break;
      case DateRangeRelativeDurationRound.END:
        while (timestamp.day() !== targetDayWeekEnd) {
          timestamp.add(1, 'day');
        }
        timestamp = timestamp.endOf('day');
        break;
    }
  } else {
    switch (relativeDuration.round) {
      case DateRangeRelativeDurationRound.START:
        timestamp = timestamp.startOf(relativeDuration.unit);
        break;
      case DateRangeRelativeDurationRound.END:
        timestamp = timestamp.endOf(relativeDuration.unit);
        break;
    }
  }
  return timestamp;
}
